/**
 * ShipmentStatusReportView.js
 * ─────────────────────────────────────────────────────────────────────────────
 * PURPOSE
 *   Displays a cross-reference report showing every active Purchase Order (PO)
 *   and Sales Order (SO) together with flags indicating whether they have:
 *     • a Shipment Request (SR)
 *     • a Shipping Instruction (SI)
 *
 * HOW THE DATA FLOWS
 *   1. On mount (or on Refresh) → one GET call to
 *      /order-view/shipment-status-report   (optional ?orderType=VENDOR/CUSTOMER)
 *      returns all active orders with hasSr / hasSi boolean flags computed
 *      server-side using PostgreSQL EXISTS sub-queries.
 *   2. All subsequent filtering (SR status, SI status, stakeholder, search text)
 *      is done entirely on the client — no extra network calls needed.
 *      This gives instant, snappy filter responses.
 *
 * FILTERS
 *   ┌─────────────────────────────────────────────────────────────────────┐
 *   │  Order Type   : All  │  Purchase Orders (PO)  │  Sales Orders (SO)  │
 *   │  SR Status    : All  │  Has SR                │  No SR              │
 *   │  SI Status    : All  │  Has SI                │  No SI              │
 *   │  Stakeholder  : Autocomplete (derived from loaded data)             │
 *   │  Search       : Text field (matches order #, ref #, stakeholder,   │
 *   │                             description)                            │
 *   └─────────────────────────────────────────────────────────────────────┘
 *
 * TABLE COLUMNS
 *   Order #  │  Type  │  Ref. Number  │  Stakeholder  │  Description  │
 *   Volume   │  Price │  Op. Date     │  ETD          │  Status       │
 *   Has SR   │  Has SI
 *
 * EXCEL EXPORT
 *   Uses ExcelJS to generate an .xlsx file from whatever rows are currently
 *   visible after all client-side filters are applied.
 *
 * IMPORTANT TERMINOLOGY (used throughout this file)
 *   PO  = Purchase Order  → backend orderType = "VENDOR"
 *   SO  = Sales Order     → backend orderType = "CUSTOMER"
 *   SR  = Shipment Request
 *   SI  = Shipping Instruction
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

import { format } from "date-fns";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import MainCard from "../../../components/MainCard";
import { retrieveShipmentStatusReportApi } from "../../../components/api/OrderApiService";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Column definitions for the table.
 * id        – key used for sorting (must match the field name we compute in normaliseRow())
 * label     – visible header text
 * align     – cell alignment
 * sortable  – whether the column shows a sort arrow
 * minWidth  – helps keep the table readable on smaller screens
 */
const COLUMNS = [
  { id: "orderId",          label: "Order ID",      align: "left",  sortable: true,  minWidth: 80  },
  //{ id: "orderRef",         label: "Order #",       align: "left",  sortable: true,  minWidth: 90  },
  { id: "orderType",        label: "Type",          align: "left",  sortable: true,  minWidth: 60  },
  { id: "refNumber",        label: "SAP Number",    align: "left",  sortable: true,  minWidth: 140 },
  { id: "stakeholderName",  label: "Stakeholder",   align: "left",  sortable: true,  minWidth: 150 },
  { id: "descriptionOfGoods", label: "Description", align: "left",  sortable: false, minWidth: 160 },
  { id: "volume",           label: "Volume",        align: "right", sortable: true,  minWidth: 90  },
  { id: "remainingQuantity",label: "Remaining Vol.",align: "right", sortable: true,  minWidth: 110 },
  { id: "usdFinalPrice",    label: "Unit Price (USD)", align: "right", sortable: true, minWidth: 110 },
  { id: "usdTotalAmount",   label: "Total (USD)",   align: "right", sortable: true,  minWidth: 120 },
  { id: "usdRemainingAmount",label:"Remaining (USD)",align: "right", sortable: true,  minWidth: 120 },
  { id: "operationDate",    label: "Op. Date",      align: "left",  sortable: true,  minWidth: 100 },
  { id: "etd",              label: "ETD",           align: "left",  sortable: true,  minWidth: 100 },
  { id: "orderStatus",      label: "Status",        align: "left",  sortable: true,  minWidth: 110 },
  { id: "hasSr",            label: "Has SR",        align: "center",sortable: true,  minWidth: 70  },
  { id: "srIds",            label: "SR ID(s)",      align: "left",  sortable: false, minWidth: 80  },
  { id: "hasSi",            label: "Has SI",        align: "center",sortable: true,  minWidth: 70  },
  { id: "siIds",            label: "SI ID(s)",      align: "left",  sortable: false, minWidth: 80  },
  { id: "siNumbers",        label: "SI Number(s)",  align: "left",  sortable: false, minWidth: 140 },
];

/**
 * Order-type filter options.
 * value=null means "both PO and SO" — used when making the API call.
 */
const ORDER_TYPE_OPTIONS = [
  { value: null,       label: "All" },
  { value: "VENDOR",   label: "PO" },   // Purchase Order
  { value: "CUSTOMER", label: "SO" },   // Sales Order
];

/**
 * Shipment Request (SR) filter options.
 * null = no filter, true = has SR, false = no SR.
 */
const SR_FILTER_OPTIONS = [
  { value: null,  label: "All"    },
  { value: true,  label: "Has SR" },
  { value: false, label: "No SR"  },
];

/**
 * Shipping Instruction (SI) filter options.
 * null = no filter, true = has SI, false = no SI.
 */
const SI_FILTER_OPTIONS = [
  { value: null,  label: "All"    },
  { value: true,  label: "Has SI" },
  { value: false, label: "No SI"  },
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/** Safely formats a date value (string, timestamp, or Date object) to dd.MM.yyyy */
function fmtDate(val) {
  if (!val) return "";
  try {
    return format(new Date(val), "dd.MM.yyyy");
  } catch {
    return "";
  }
}

/** Safely formats a number with 2 decimal places; returns "" for null/undefined */
function fmtNum(val) {
  if (val == null) return "";
  const n = Number(val);
  return Number.isFinite(n) ? n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";
}

/**
 * Converts a raw row from the API response into a flat, display-ready object.
 *
 * Why normalise?  The API returns camelCase fields from a Spring projection.
 * We add derived fields like orderRef, refNumber, and etd here once,
 * so sorting and searching don't have to repeat this logic.
 *
 * @param {object} row - raw API row (OrderShipmentStatusView shape)
 * @returns {object} normalised row ready for display / export
 */
function normaliseRow(row) {
  // Build a human-readable order reference: "2024/42"
  const orderRef =
    row.orderYear && row.orderGroupNumber
      ? `${row.orderYear}/${row.orderGroupNumber}`
      : `#${row.orderId}`;

  // SAP number: purchaseOrderNumber for POs, salesOrderNumber for SOs
  const refNumber =
    row.orderType === "VENDOR"
      ? (row.purchaseOrderNumber || "")
      : (row.salesOrderNumber    || "");

  // Short readable label for the type column
  const orderTypeLabel = row.orderType === "VENDOR" ? "PO" : "SO";

  return {
    ...row,
    // derived / display-only
    orderRef,
    refNumber,
    orderTypeLabel,
    etd: row.estimatedTimeOfDeparture || null,
    // Coerce booleans — the DTO already returns true JS booleans via Jackson,
    // but we guard here in case of any serialisation quirk.
    hasSr: Boolean(row.hasSr),
    hasSi: Boolean(row.hasSi),
    // String IDs from STRING_AGG — null when nothing linked
    srIds:    row.srIds    || null,
    siIds:    row.siIds    || null,
    siNumbers:row.siNumbers|| null,
    // Price renamed for clarity
    usdFinalPrice:      row.usdFinalPrice      || null,
    // Amount fields (computed server-side as volume/remainingQty * usdFinalPrice)
    remainingQuantity:  row.remainingQuantity  ?? null,
    usdTotalAmount:     row.usdTotalAmount     ?? null,
    usdRemainingAmount: row.usdRemainingAmount ?? null,
  };
}

/**
 * Generic stable sort that preserves original order when values are equal.
 * Used for table column sorting.
 */
function stableSort(array, comparator) {
  const indexed = array.map((el, idx) => [el, idx]);
  indexed.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    return order !== 0 ? order : a[1] - b[1];
  });
  return indexed.map(([el]) => el);
}

/** Returns a comparator function for ascending / descending sort by a given field. */
function getComparator(direction, field) {
  return direction === "desc"
    ? (a, b) => desc(a, b, field)
    : (a, b) => -desc(a, b, field);
}

function desc(a, b, field) {
  const av = a[field] ?? "";
  const bv = b[field] ?? "";
  if (bv < av) return -1;
  if (bv > av) return  1;
  return 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function ShipmentStatusReportView() {
  const theme = useTheme();

  // ── Raw data from the API (all active orders) ──────────────────────────────
  const [rows, setRows] = useState([]); // normalised rows (see normaliseRow())
  const [loading, setLoading] = useState(false);

  // ── Filters ────────────────────────────────────────────────────────────────
  /**
   * orderTypeFilter: null | "VENDOR" | "CUSTOMER"
   * Changing this re-fetches from the server (the server filters by type).
   * This is the one filter that is handled server-side because it affects
   * which data is fetched — all other filters work on the already-loaded data.
   */
  const [orderTypeFilter, setOrderTypeFilter] = useState(null);

  /**
   * srFilter: null | true | false
   * Client-side filter: show all / only orders with SR / only orders without SR.
   */
  const [srFilter, setSrFilter]   = useState(null);

  /**
   * siFilter: null | true | false
   * Client-side filter: show all / only orders with SI / only orders without SI.
   */
  const [siFilter, setSiFilter]   = useState(null);

  /**
   * selectedStakeholders: array of stakeholder name strings chosen in autocomplete.
   * Derived from the loaded data, so no extra API call is needed.
   */
  const [selectedStakeholders, setSelectedStakeholders] = useState([]);

  /**
   * searchText: free-text search applied across order #, ref #, stakeholder name,
   * and description of goods (case-insensitive).
   */
  const [searchText, setSearchText] = useState("");

  // ── Table state ────────────────────────────────────────────────────────────
  const [sortDir,    setSortDir]    = useState("desc");  // "asc" | "desc"
  const [sortField,  setSortField]  = useState("orderRef"); // column id
  const [page,       setPage]       = useState(0);
  const [rowsPerPage,setRowsPerPage]= useState(25);

  // ─────────────────────────────────────────────────────────────────────────
  // DATA FETCHING
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * fetchData is called on mount and whenever the user clicks Refresh.
   * It also fires when orderTypeFilter changes, because that filter must
   * be applied server-side (the others are client-side).
   */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Pass the order-type filter to the server (null = both PO and SO)
      const { data } = await retrieveShipmentStatusReportApi(orderTypeFilter);
      const normalised = Array.isArray(data) ? data.map(normaliseRow) : [];
      setRows(normalised);
      // Reset to page 0 whenever fresh data arrives
      setPage(0);
    } catch (err) {
      console.error("ShipmentStatusReport – fetch error:", err);
      toast.error("Failed to load report data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [orderTypeFilter]);

  // Auto-fetch on mount and whenever the server-side filter changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─────────────────────────────────────────────────────────────────────────
  // DERIVED DATA — stakeholder options for the autocomplete
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Build a unique, sorted list of stakeholder names from the loaded rows.
   * This replaces a separate "load stakeholders" API call — we simply use
   * the stakeholders that actually appear in the current data.
   */
  const stakeholderOptions = useMemo(() => {
    const names = new Set();
    rows.forEach((r) => {
      if (r.stakeholderName) names.add(r.stakeholderName);
    });
    return Array.from(names).sort();
  }, [rows]);

  // ─────────────────────────────────────────────────────────────────────────
  // FILTERED + SORTED ROWS  (all client-side)
  // ─────────────────────────────────────────────────────────────────────────

  const filteredRows = useMemo(() => {
    let result = rows;

    // 1. SR filter — show only orders that have / don't have a shipment request
    if (srFilter !== null) {
      result = result.filter((r) => r.hasSr === srFilter);
    }

    // 2. SI filter — show only orders that have / don't have a shipping instruction
    if (siFilter !== null) {
      result = result.filter((r) => r.hasSi === siFilter);
    }

    // 3. Stakeholder filter — multi-select from the autocomplete
    if (selectedStakeholders.length > 0) {
      result = result.filter((r) =>
        selectedStakeholders.includes(r.stakeholderName)
      );
    }

    // 4. Free-text search — checked against order #, ref #, stakeholder, description
    if (searchText.trim()) {
      const needle = searchText.toLowerCase();
      result = result.filter(
        (r) =>
          (r.orderRef         || "").toLowerCase().includes(needle) ||
          (r.refNumber        || "").toLowerCase().includes(needle) ||
          (r.stakeholderName  || "").toLowerCase().includes(needle) ||
          (r.descriptionOfGoods || "").toLowerCase().includes(needle)
      );
    }

    // 5. Stable sort by the selected column
    return stableSort(result, getComparator(sortDir, sortField));
  }, [rows, srFilter, siFilter, selectedStakeholders, searchText, sortDir, sortField]);

  // ─────────────────────────────────────────────────────────────────────────
  // TABLE SORT HANDLER
  // ─────────────────────────────────────────────────────────────────────────

  /** Clicking the same column header toggles direction; clicking a new one sorts desc. */
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
    setPage(0); // jump back to page 1 after sorting
  };

  // ─────────────────────────────────────────────────────────────────────────
  // FILTER CLEAR
  // ─────────────────────────────────────────────────────────────────────────

  /** Resets every client-side filter back to its default (show-all) state. */
  const clearFilters = () => {
    setSrFilter(null);
    setSiFilter(null);
    setSelectedStakeholders([]);
    setSearchText("");
    setPage(0);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // EXCEL EXPORT
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Exports the currently-visible (filtered) rows to an .xlsx file.
   *
   * Column order in the Excel file matches the visible table columns.
   * Boolean SR/SI flags are written as "Yes" / "No" for readability.
   * Dates are formatted as dd.MM.yyyy strings.
   * Numbers are written as actual numbers so Excel can do arithmetic on them.
   */
  const exportToExcel = async () => {
    try {
      const workbook  = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Shipment Status Report");

      // ── Header row styling ──────────────────────────────────────────────
      const headerFill = {
        type: "pattern", pattern: "solid",
        fgColor: { argb: "FF425078" }, // dark navy (matches app theme)
      };
      const headerFont = { color: { argb: "FFFFFFFF" }, bold: true, size: 11 };

      worksheet.columns = [
        { header: "Order ID",      key: "orderId",            width: 10 },
        //{ header: "Order #",       key: "orderRef",           width: 12 },
        { header: "Type",          key: "orderTypeLabel",     width: 8  },
        { header: "SAP Number",    key: "refNumber",          width: 22 },
        { header: "Stakeholder",   key: "stakeholderName",    width: 25 },
        { header: "Description",   key: "descriptionOfGoods", width: 30 },
        { header: "Volume",        key: "volume",             width: 14 },
        { header: "Unit",          key: "volumeUnit",         width: 8  },
        { header: "Remaining Vol.",key: "remainingQuantity",  width: 14 },
        { header: "Unit Price USD",key: "usdFinalPrice",      width: 14 },
        { header: "Total (USD)",   key: "usdTotalAmount",     width: 16 },
        { header: "Remaining (USD)",key:"usdRemainingAmount", width: 16 },
        { header: "Op. Date",      key: "operationDate",      width: 12 },
        { header: "ETD",           key: "etd",                width: 12 },
        { header: "Order Status",  key: "orderStatus",        width: 18 },
        { header: "Has SR",        key: "hasSr",              width: 9  },
        { header: "SR ID(s)",      key: "srIds",              width: 14 },
        { header: "Has SI",        key: "hasSi",              width: 9  },
        { header: "SI ID(s)",      key: "siIds",              width: 14 },
        { header: "SI Number(s)",  key: "siNumbers",          width: 25 },
      ];

      // Style the header row
      worksheet.getRow(1).eachCell((cell) => {
        cell.fill = headerFill;
        cell.font = headerFont;
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          bottom: { style: "thin", color: { argb: "FF000000" } },
        };
      });

      // ── Data rows ───────────────────────────────────────────────────────
      filteredRows.forEach((r) => {
        worksheet.addRow({
          orderId:            r.orderId,
          //orderRef:           r.orderRef,
          orderTypeLabel:     r.orderTypeLabel,
          refNumber:          r.refNumber,
          stakeholderName:    r.stakeholderName    || "",
          descriptionOfGoods: r.descriptionOfGoods || "",
          volume:             r.volume      != null ? Number(r.volume)        : "",
          volumeUnit:         r.volumeUnit  || "",
          // written as a number so Excel can do arithmetic
          usdFinalPrice:      r.usdFinalPrice != null ? Number(r.usdFinalPrice) : "",
          operationDate:      fmtDate(r.operationDate),
          etd:                fmtDate(r.etd),
          orderStatus:        r.orderStatus  || "",
          hasSr:              r.hasSr ? "Yes" : "No",
          srIds:              r.srIds        || "",
          hasSi:              r.hasSi ? "Yes" : "No",
          siIds:              r.siIds        || "",
          siNumbers:          r.siNumbers    || "",
          // amounts as real numbers so Excel can sum/average them
          remainingQuantity:  r.remainingQuantity  != null ? Number(r.remainingQuantity)  : "",
          usdTotalAmount:     r.usdTotalAmount     != null ? Number(r.usdTotalAmount)     : "",
          usdRemainingAmount: r.usdRemainingAmount != null ? Number(r.usdRemainingAmount) : "",
        });
      });

      // ── Freeze the header row ───────────────────────────────────────────
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      // ── Save file ───────────────────────────────────────────────────────
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        `Shipment_Status_Report_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`
      );
    } catch (err) {
      console.error("Excel export error:", err);
      toast.error("Excel export failed.");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SUMMARY COUNTS  (displayed below the filters for quick reference)
  // ─────────────────────────────────────────────────────────────────────────

  const counts = useMemo(() => {
    const total   = filteredRows.length;
    const hasSrN  = filteredRows.filter((r) => r.hasSr).length;
    const noSrN   = total - hasSrN;
    const hasSiN  = filteredRows.filter((r) => r.hasSi).length;
    const noSiN   = total - hasSiN;
    return { total, hasSrN, noSrN, hasSiN, noSiN };
  }, [filteredRows]);

  // ─────────────────────────────────────────────────────────────────────────
  // STYLED HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Renders a Chip for the Has SR / Has SI columns.
   * Green when true (has SR/SI), light amber when false (no SR/SI).
   */
  const BoolChip = ({ value, trueLabel, falseLabel }) => (
    <Chip
      label={value ? trueLabel : falseLabel}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.72rem",
        // use theme-aware colours so the chip works in both light/dark mode
        bgcolor: value
          ? theme.palette.success.light
          : theme.palette.warning.light,
        color: value
          ? theme.palette.success.dark
          : theme.palette.warning.dark,
      }}
    />
  );

  /**
   * Renders a small Chip for the order type column.
   * Blue for PO (VENDOR), teal for SO (CUSTOMER).
   */
  const TypeChip = ({ type }) => (
    <Chip
      label={type === "VENDOR" ? "PO" : "SO"}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.72rem",
        bgcolor: type === "VENDOR"
          ? theme.palette.primary.light
          : theme.palette.secondary.light,
        color: type === "VENDOR"
          ? theme.palette.primary.dark
          : theme.palette.secondary.dark,
      }}
    />
  );

  // ─────────────────────────────────────────────────────────────────────────
  // PAGINATION SLICE
  // ─────────────────────────────────────────────────────────────────────────

  /** The rows visible on the current page */
  const pageRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <MainCard
      title={
        <Typography variant="h4" fontWeight={700}>
          PO / SO Shipment Status Report
        </Typography>
      }
    >
      {/* ════════════════════════════════════════════════════════════════════
          FILTER PANEL
          ════════════════════════════════════════════════════════════════════ */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2, borderRadius: 2, background: theme.palette.background.default }}
      >
        <Stack spacing={2}>

          {/* Row 1 — toggle buttons + action buttons */}
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={2}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* ── Left side: three toggle groups ── */}
            <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">

              {/* Order Type filter (triggers a server re-fetch on change) */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                  Order Type
                </Typography>
                <ToggleButtonGroup
                  value={orderTypeFilter}
                  exclusive
                  size="small"
                  onChange={(_, val) => {
                    // ToggleButtonGroup passes null when the active button is clicked again —
                    // we treat that as "All" to avoid ending up with no selection.
                    setOrderTypeFilter(val);   // triggers re-fetch via useEffect
                    setPage(0);
                  }}
                >
                  {ORDER_TYPE_OPTIONS.map((opt) => (
                    <ToggleButton
                      key={String(opt.value)}
                      value={opt.value}
                      sx={{ px: 2, fontWeight: 600, minWidth: 50 }}
                    >
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              {/* SR filter (client-side only) */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                  Shipment Request
                </Typography>
                <ToggleButtonGroup
                  value={srFilter}
                  exclusive
                  size="small"
                  onChange={(_, val) => { setSrFilter(val); setPage(0); }}
                >
                  {SR_FILTER_OPTIONS.map((opt) => (
                    <ToggleButton
                      key={String(opt.value)}
                      value={opt.value}
                      sx={{ px: 2, fontWeight: 600, minWidth: 70 }}
                    >
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              {/* SI filter (client-side only) */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                  Shipping Instruction
                </Typography>
                <ToggleButtonGroup
                  value={siFilter}
                  exclusive
                  size="small"
                  onChange={(_, val) => { setSiFilter(val); setPage(0); }}
                >
                  {SI_FILTER_OPTIONS.map((opt) => (
                    <ToggleButton
                      key={String(opt.value)}
                      value={opt.value}
                      sx={{ px: 2, fontWeight: 600, minWidth: 70 }}
                    >
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </Stack>

            {/* ── Right side: action buttons ── */}
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Clear all client-side filters */}
              <Tooltip title="Clear all filters">
                <IconButton size="small" onClick={clearFilters} color="default">
                  <FilterAltOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Re-fetch data from server */}
              <Tooltip title="Refresh data">
                <IconButton
                  size="small"
                  onClick={fetchData}
                  color="primary"
                  disabled={loading}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Export visible rows to Excel */}
              <Tooltip title="Export to Excel">
                <IconButton
                  size="small"
                  onClick={exportToExcel}
                  color="success"
                  disabled={filteredRows.length === 0}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Row 2 — stakeholder autocomplete + text search */}
          <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">

            {/* Stakeholder autocomplete — options are derived from loaded data */}
            <Autocomplete
              multiple
              size="small"
              options={stakeholderOptions}
              value={selectedStakeholders}
              onChange={(_, val) => { setSelectedStakeholders(val); setPage(0); }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Stakeholder"
                  placeholder="Filter by stakeholder…"
                />
              )}
              sx={{ minWidth: 280, maxWidth: 450 }}
              // limit chips shown to keep the bar compact
              limitTags={3}
            />

            {/* Free-text search */}
            <TextField
              size="small"
              label="Search"
              placeholder="Order #, ref. #, stakeholder, description…"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
              sx={{ minWidth: 280 }}
            />
          </Stack>

          {/* Summary counts — quick overview of what is visible */}
          <Divider />
          <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Showing <strong>{counts.total}</strong> orders —
            </Typography>
            <Chip size="small" label={`${counts.hasSrN} with SR`}
              sx={{ bgcolor: theme.palette.success.light, color: theme.palette.success.dark, fontWeight: 600 }} />
            <Chip size="small" label={`${counts.noSrN} without SR`}
              sx={{ bgcolor: theme.palette.warning.light, color: theme.palette.warning.dark, fontWeight: 600 }} />
            <Typography variant="body2" color="text.secondary">•</Typography>
            <Chip size="small" label={`${counts.hasSiN} with SI`}
              sx={{ bgcolor: theme.palette.success.light, color: theme.palette.success.dark, fontWeight: 600 }} />
            <Chip size="small" label={`${counts.noSiN} without SI`}
              sx={{ bgcolor: theme.palette.warning.light, color: theme.palette.warning.dark, fontWeight: 600 }} />
          </Stack>

        </Stack>
      </Paper>

      {/* ════════════════════════════════════════════════════════════════════
          RESULTS TABLE
          ════════════════════════════════════════════════════════════════════ */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" stickyHeader>

          {/* ── Table Head ── */}
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  style={{ minWidth: col.minWidth }}
                  sortDirection={sortField === col.id ? sortDir : false}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortField === col.id}
                      direction={sortField === col.id ? sortDir : "asc"}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                      {/* Screen-reader only text for accessibility */}
                      {sortField === col.id && (
                        <Box component="span" sx={visuallyHidden}>
                          {sortDir === "desc" ? "sorted descending" : "sorted ascending"}
                        </Box>
                      )}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* ── Table Body ── */}
          <TableBody>
            {loading ? (
              // While data is loading show a single row with a message
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Loading…</Typography>
                </TableCell>
              </TableRow>
            ) : pageRows.length === 0 ? (
              // No results after filtering
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No records match the current filters.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => (
                <TableRow
                  key={row.orderId}
                  hover
                  sx={{
                    // Subtle highlight for rows that are missing BOTH SR and SI
                    // so the user can spot the most critical gaps at a glance.
                    ...((!row.hasSr && !row.hasSi) && {
                      backgroundColor: theme.palette.action.hover,
                    }),
                  }}
                >
                  {/* Order ID — database PK, useful for cross-referencing */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                      {row.orderId}
                    </Typography>
                  </TableCell>

                  {/* Order # = year / group number 
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.orderRef}
                    </Typography>
                  </TableCell>*/}

                  {/* Type chip (PO / SO) */}
                  <TableCell>
                    <TypeChip type={row.orderType} />
                  </TableCell>

                  {/* SAP number (purchaseOrderNumber or salesOrderNumber) */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{row.refNumber || "—"}</Typography>
                  </TableCell>

                  {/* Stakeholder name */}
                  <TableCell>
                    <Typography variant="body2">{row.stakeholderName || "—"}</Typography>
                  </TableCell>

                  {/* Description of goods — truncated to keep rows compact */}
                  <TableCell>
                    <Tooltip title={row.descriptionOfGoods || ""} placement="top">
                      <Typography
                        variant="body2"
                        sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {row.descriptionOfGoods || "—"}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Volume */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {fmtNum(row.volume)}
                      {row.volumeUnit ? ` ${row.volumeUnit}` : ""}
                    </Typography>
                  </TableCell>

                  {/* Remaining / unshipped volume */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {row.remainingQuantity != null ? `${fmtNum(row.remainingQuantity)}${row.volumeUnit ? ` ${row.volumeUnit}` : ""}` : "—"}
                    </Typography>
                  </TableCell>

                  {/* Final unit price in USD */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {row.usdFinalPrice != null ? `${fmtNum(row.usdFinalPrice)} USD` : "—"}
                    </Typography>
                  </TableCell>

                  {/* Total USD amount (volume × unit price — computed in SQL) */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {row.usdTotalAmount != null ? `${fmtNum(row.usdTotalAmount)} USD` : "—"}
                    </Typography>
                  </TableCell>

                  {/* Remaining USD amount (remaining volume × unit price) */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {row.usdRemainingAmount != null ? `${fmtNum(row.usdRemainingAmount)} USD` : "—"}
                    </Typography>
                  </TableCell>

                  {/* Operation date */}
                  <TableCell>
                    <Typography variant="body2">{fmtDate(row.operationDate) || "—"}</Typography>
                  </TableCell>

                  {/* ETD — estimated time of departure */}
                  <TableCell>
                    <Typography variant="body2">{fmtDate(row.etd) || "—"}</Typography>
                  </TableCell>

                  {/* Order status */}
                  <TableCell>
                    <Typography variant="body2">{row.orderStatus || "—"}</Typography>
                  </TableCell>

                  {/* Has SR chip */}
                  <TableCell align="center">
                    <BoolChip value={row.hasSr} trueLabel="Yes" falseLabel="No" />
                  </TableCell>

                  {/* SR IDs — comma-separated list from STRING_AGG in the query */}
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                      {row.srIds || "—"}
                    </Typography>
                  </TableCell>

                  {/* Has SI chip */}
                  <TableCell align="center">
                    <BoolChip value={row.hasSi} trueLabel="Yes" falseLabel="No" />
                  </TableCell>

                  {/* SI IDs */}
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                      {row.siIds || "—"}
                    </Typography>
                  </TableCell>

                  {/* SI final shipping instruction numbers */}
                  <TableCell>
                    <Typography variant="body2">{row.siNumbers || "—"}</Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ── */}
      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Rows per page:"
      />
    </MainCard>
  );
}
